import { useEffect, useState } from 'react';
import * as emailClient from './api/email-settings-client';
import type { EmailSettingsState, EmailSettingsPutBody, EmailTestResult } from './api/email-settings-client';
import { ApiError } from '@/lib/api-client';
import { showApiError } from '@/lib/error-toast';
import styles from './tenant-settings.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 20;

export function EmailSection() {
  const [state, setState] = useState<EmailSettingsState | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  // Task 12 "Clear stored password" — staged locally; only sent as
  // `password: null` on the next Save. Typing a new password below is
  // impossible while staged (the input is replaced by the staged notice),
  // so the two never race.
  const [clearStaged, setClearStaged] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<EmailTestResult | null>(null);
  // Dead-row id whose redrive request is in flight (v1.1) — disables that
  // row's Retry button so a double-click can't fire two redrives.
  const [redriving, setRedriving] = useState<string | null>(null);

  async function loadAll() {
    const cfg = await emailClient.get();
    setState(cfg);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!state) return;
    setSaving(true);
    try {
      const body: EmailSettingsPutBody = {
        enabled: state.enabled,
        host: state.host,
        port: state.port,
        secure: state.secure,
        username: state.username,
        fromAddress: state.fromAddress,
        fromName: state.fromName,
        opsRecipients: state.opsRecipients,
        // Omitted → preserve. `null` → clear (staged). A typed value → rotate.
        ...(clearStaged ? { password: null } : passwordInput ? { password: passwordInput } : {}),
      };
      await emailClient.put(body);
      setPasswordInput('');
      setClearStaged(false);
      await loadAll();
    } catch (err) {
      showApiError(err, { fallback: 'Failed to save email settings' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await emailClient.test();
      setTestResult(result);
    } catch (err) {
      // The /test endpoint's 60s per-tenant cooldown surfaces as a 429 with
      // { ok:false, error:"cooldown", retryInSeconds } — api-client throws
      // that as an ApiError rather than resolving it, so without this catch
      // it was an unhandled rejection and the button just flickered back to
      // idle with no feedback (review finding, Task 12).
      if (err instanceof ApiError && err.status === 429) {
        const body = err.body as { retryInSeconds?: number } | null;
        const seconds = body?.retryInSeconds ?? 60;
        setTestResult({ ok: false, error: `Please wait ${seconds}s before sending another test` });
      } else {
        showApiError(err, { fallback: 'Failed to send test email' });
      }
    } finally {
      setTesting(false);
    }
  }

  // Shared by Enter and blur (v1.1 commit-on-blur: typed-but-not-entered
  // text used to be silently dropped, e.g. when clicking straight to Save).
  function commitRecipient() {
    if (!state) return;

    const trimmed = recipientInput.trim();
    if (!trimmed) return;

    // All three error branches deliberately leave `recipientInput` in place
    // (keep-with-invalid-state) so the operator can see and correct what
    // they typed, rather than having it silently vanish (review finding,
    // Task 12 minor #4). Only a successful add clears the field.
    if (!EMAIL_RE.test(trimmed)) {
      setRecipientError('Invalid email address');
      return;
    }
    if (state.opsRecipients.includes(trimmed)) {
      setRecipientError('Already added');
      return;
    }
    if (state.opsRecipients.length >= MAX_RECIPIENTS) {
      setRecipientError(`Maximum ${MAX_RECIPIENTS} recipients`);
      return;
    }

    setState({ ...state, opsRecipients: [...state.opsRecipients, trimmed] });
    setRecipientInput('');
    setRecipientError(null);
  }

  function handleRecipientKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    commitRecipient();
  }

  async function handleRedrive(id: string) {
    setRedriving(id);
    try {
      await emailClient.redriveOutboxRow(id);
      // Reload so the row drops off the dead list (it's pending again).
      await loadAll();
    } catch (err) {
      showApiError(err, { fallback: 'Failed to retry the email' });
    } finally {
      setRedriving(null);
    }
  }

  function handleRemoveRecipient(email: string) {
    if (!state) return;
    setState({ ...state, opsRecipients: state.opsRecipients.filter((r) => r !== email) });
  }

  // The parent <section className={styles.section}><h2>Email notifications</h2>
  // already labels this surface, so we render only the form (matches the
  // GithubBackupSection convention — no doubled-heading IA noise, #81).
  if (!state) {
    return <p>Loading…</p>;
  }

  return (
    <form onSubmit={handleSave} className={styles.backupForm}>
      <div className={styles.formRow}>
        <label htmlFor="email-enabled" className={styles.checkboxLabel}>
          <input
            id="email-enabled"
            type="checkbox"
            checked={state.enabled}
            onChange={(e) => setState({ ...state, enabled: e.target.checked })}
          />
          Enable email notifications
        </label>
      </div>

      {/* E-070 — a switched-off config silently no-ops every producer
          (`enqueueEmail` returns "skipped"), which reads as "email is broken"
          rather than "email is off". Say which one it is. */}
      {!state.enabled && (
        <p className={styles.failuresLine}>
          Email notifications are disabled — notify rules and system alerts will not send.
        </p>
      )}

      {/* E-070 — the failure surface renders only when failures exist, so an
          empty one was indistinguishable from a missing feature. Empty is a
          state; name it. */}
      {state.recentFailures.count7d > 0 ? (
        <p className={styles.failuresLine}>
          {`${state.recentFailures.count7d} emails failed in the last 7 days — last error: ${state.recentFailures.lastError ?? 'unknown'}`}
        </p>
      ) : (
        <p className={styles.failuresLine}>No failed emails in the last 7 days.</p>
      )}

      {(state.recentFailures.deadRows ?? []).length > 0 && (
        <ul className={styles.deadRowList}>
          {state.recentFailures.deadRows.map((row) => (
            <li key={row.id} className={styles.deadRow}>
              <span className={styles.deadRowText}>
                {`${row.kind.replaceAll('_', ' ')} — ${row.lastError ?? 'unknown error'} (${new Date(row.updatedAt).toLocaleString()})`}
              </span>
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={redriving === row.id}
                onClick={() => void handleRedrive(row.id)}
              >
                {redriving === row.id ? 'Retrying…' : 'Retry'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.formGrid}>
        <label htmlFor="email-host" className={styles.fieldLabel}>
          SMTP host
        </label>
        <input
          id="email-host"
          type="text"
          value={state.host}
          onChange={(e) => setState({ ...state, host: e.target.value })}
          className={styles.textInput}
        />

        <label htmlFor="email-port" className={styles.fieldLabel}>
          Port
        </label>
        <input
          id="email-port"
          type="number"
          value={state.port}
          onChange={(e) => setState({ ...state, port: Number(e.target.value) || 0 })}
          className={styles.textInput}
        />

        <label htmlFor="email-secure" className={styles.fieldLabel}>
          Secure (TLS)
        </label>
        <input
          id="email-secure"
          type="checkbox"
          checked={state.secure}
          onChange={(e) => setState({ ...state, secure: e.target.checked })}
        />

        <label htmlFor="email-username" className={styles.fieldLabel}>
          Username
        </label>
        <input
          id="email-username"
          type="text"
          value={state.username}
          onChange={(e) => setState({ ...state, username: e.target.value })}
          className={styles.textInput}
        />

        <label htmlFor="email-password" className={styles.fieldLabel}>
          Password
        </label>
        {clearStaged ? (
          <div className={styles.actionRow}>
            <span className={styles.clearStaged}>Password will be cleared on save</span>
            <button
              type="button"
              onClick={() => setClearStaged(false)}
              className={styles.secondaryButton}
            >
              Undo
            </button>
          </div>
        ) : (
          <div className={styles.actionRow}>
            <input
              id="email-password"
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder={state.hasPassword ? '••••••• (saved)' : 'Enter SMTP password'}
              className={styles.textInput}
            />
            {state.hasPassword && (
              <button
                type="button"
                onClick={() => {
                  setClearStaged(true);
                  setPasswordInput('');
                }}
                className={styles.secondaryButton}
              >
                Clear
              </button>
            )}
          </div>
        )}

        <label htmlFor="email-from-address" className={styles.fieldLabel}>
          From address
        </label>
        <input
          id="email-from-address"
          type="email"
          value={state.fromAddress}
          onChange={(e) => setState({ ...state, fromAddress: e.target.value })}
          className={styles.textInput}
        />

        <label htmlFor="email-from-name" className={styles.fieldLabel}>
          From name
        </label>
        <input
          id="email-from-name"
          type="text"
          value={state.fromName}
          onChange={(e) => setState({ ...state, fromName: e.target.value })}
          className={styles.textInput}
        />

        <label htmlFor="email-ops-recipients" className={styles.fieldLabel}>
          Ops recipients
        </label>
        <div>
          {state.opsRecipients.length > 0 && (
            <div className={styles.chipList}>
              {state.opsRecipients.map((r) => (
                <span key={r} className={styles.chip}>
                  {r}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => handleRemoveRecipient(r)}
                    aria-label={`Remove ${r}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            id="email-ops-recipients"
            type="text"
            value={recipientInput}
            onChange={(e) => {
              setRecipientInput(e.target.value);
              setRecipientError(null);
            }}
            onKeyDown={handleRecipientKeyDown}
            onBlur={commitRecipient}
            placeholder="ops@example.com — press Enter to add"
            className={styles.textInput}
          />
          {recipientError && <span className={styles.chipError}>{recipientError}</span>}
        </div>
      </div>

      <div className={styles.actionRow}>
        <button type="submit" disabled={saving} className={styles.primaryButton}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          disabled={testing}
          onClick={handleTest}
          className={styles.secondaryButton}
        >
          {testing ? 'Sending…' : 'Send test email'}
        </button>
        {testResult && (
          <span className={testResult.ok ? styles.testOk : styles.testErr}>
            {testResult.ok ? '✓ ok' : testResult.error ?? 'error'}
          </span>
        )}
      </div>
    </form>
  );
}
